import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http'
import { inject } from '@angular/core'
import { BehaviorSubject, catchError, finalize, switchMap, take, throwError } from 'rxjs'
import { filter } from 'rxjs/operators'
import { TokenService } from '../services/token.service'
import { AuthSessionEventsService } from '../services/auth-session-events.service'
import { UserTranslation } from '../models/user.model'
import { AuthService } from '@monorepo/translation-system-generated'
// Shared state across interceptor calls
let isRefreshingToken = false
const tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null)

export const AuthTranslationInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService)
  const authService = inject(AuthService)
  const authSessionEvents = inject(AuthSessionEventsService)

  const accessToken = tokenService.getAccessToken() ?? ''
  return next(handleRequest(req, accessToken)).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        return handle401Error(req, next)
      }
      return throwError(() => error)
    }),
  )

  function handleRequest(req: HttpRequest<any>, token: string): HttpRequest<any> {
    if (req.url.includes('refresh')) return req
    return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
  }

  function handle401Error(req: HttpRequest<any>, next: HttpHandlerFn) {
    if (req.url.includes('refresh')) {
      return logoutUser()
    }

    if (!isRefreshingToken) {
      isRefreshingToken = true
      tokenSubject.next(null)

      const user = tokenService.getUser() as UserTranslation

      if (!user) {
        return next(handleRequest(req, ''))
      }

      return authService.authControllerRefreshToken().pipe(
        switchMap((tokens) => {
          const newToken = tokens.accessToken
          if (newToken) {
            tokenService.saveAccessToken(newToken)
            tokenSubject.next(newToken)
            return next(handleRequest(req, newToken))
          } else {
            return logoutUser()
          }
        }),
        catchError(() => {
          return logoutUser()
        }),
        finalize(() => {
          isRefreshingToken = false
        }),
      )
    } else {
      return tokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          return next(handleRequest(req, token ?? ''))
        }),
      )
    }
  }

  function logoutUser() {
    authSessionEvents.requestLogout()
    return throwError(() => new Error('Session expired, please login again.'))
  }
}
